import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Store, Search, MessageCircle, Mail, Brain, Mic,
  Calendar, CreditCard, Video, FileText, Printer,
  ClipboardList, PenTool, Check, X, Loader2, ExternalLink,
  Settings, Zap, Shield, ArrowRight, Star
} from "lucide-react";

const ICON_MAP = {
  "message-circle": MessageCircle,
  "mail": Mail,
  "brain": Brain,
  "mic": Mic,
  "calendar": Calendar,
  "credit-card": CreditCard,
  "video": Video,
  "file-text": FileText,
  "printer": Printer,
  "clipboard-list": ClipboardList,
  "pen-tool": PenTool
};

/**
 * Integration Marketplace
 * Third-party integration catalog and management
 */
export default function IntegrationMarketplace() {
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState([]);
  const [categories, setCategories] = useState([]);
  const [configured, setConfigured] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [catalogRes, configuredRes] = await Promise.all([
        api.get("/integrations/catalog"),
        api.get("/integrations/configured").catch(() => ({ data: { configured: [] } }))
      ]);

      setCatalog(catalogRes.data.integrations || []);
      setCategories(catalogRes.data.categories || []);
      setConfigured(configuredRes.data.configured || []);
    } catch (error) {
      console.error("Error loading integrations:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredIntegrations = catalog.filter(integration => {
    const matchesSearch = searchTerm === "" ||
      integration.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      integration.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === "all" || integration.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const isConfigured = (integrationId) => {
    return configured.some(c => c.integration_id === integrationId);
  };

  const handleConfigure = (integration) => {
    setSelectedIntegration(integration);
    setApiKey("");
    setShowConfigDialog(true);
  };

  const handleSaveConfig = async () => {
    if (!selectedIntegration) return;
    
    setConfiguring(true);
    try {
      await api.post("/integrations/configure", {
        integration_id: selectedIntegration.id,
        api_key: apiKey || null,
        enabled: true
      });

      toast.success(`${selectedIntegration.name} configured successfully!`);
      setShowConfigDialog(false);
      loadData();
    } catch (error) {
      toast.error("Failed to configure integration");
    } finally {
      setConfiguring(false);
    }
  };

  const handleTest = async (integrationId) => {
    setTesting(true);
    try {
      const response = await api.post(`/integrations/test/${integrationId}`);
      
      if (response.data.success) {
        toast.success(`Connection successful! Latency: ${response.data.latency_ms}ms`);
      } else {
        toast.error(response.data.message || "Connection test failed");
      }
    } catch (error) {
      toast.error("Connection test failed");
    } finally {
      setTesting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "connected":
        return <Badge className="bg-green-100 text-green-700"><Check className="w-3 h-3 mr-1" />Connected</Badge>;
      case "available":
        return <Badge className="bg-blue-100 text-blue-700">Available</Badge>;
      case "coming_soon":
        return <Badge className="bg-slate-100 text-slate-600">Coming Soon</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-[600px] flex items-center justify-center">
        <div className="text-center space-y-3">
          <Loader2 className="w-12 h-12 animate-spin text-[#4A7C6F] mx-auto" />
          <p className="text-slate-600">Loading integrations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-6" data-testid="integration-marketplace">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#4A7C6F] to-[#6B9B8A] rounded-2xl p-8 text-white">
        <div className="flex items-center gap-3 mb-4">
          <Store className="w-8 h-8" />
          <h1 className="text-3xl font-bold">Integration Marketplace</h1>
        </div>
        <p className="text-white/80 max-w-2xl">
          Extend Goodwin Hospice with powerful third-party integrations for communication, 
          billing, scheduling, and more. Connect your favorite tools in minutes.
        </p>
        <div className="flex gap-4 mt-6">
          <div className="bg-white/20 rounded-lg px-4 py-2">
            <p className="text-2xl font-bold">{configured.length}</p>
            <p className="text-sm text-white/70">Connected</p>
          </div>
          <div className="bg-white/20 rounded-lg px-4 py-2">
            <p className="text-2xl font-bold">{catalog.filter(i => i.status === "available").length}</p>
            <p className="text-sm text-white/70">Available</p>
          </div>
          <div className="bg-white/20 rounded-lg px-4 py-2">
            <p className="text-2xl font-bold">{catalog.filter(i => i.status === "coming_soon").length}</p>
            <p className="text-sm text-white/70">Coming Soon</p>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search integrations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={selectedCategory === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCategory("all")}
            className={selectedCategory === "all" ? "bg-[#4A7C6F]" : ""}
          >
            All
          </Button>
          {categories.map(cat => (
            <Button
              key={cat.id}
              variant={selectedCategory === cat.id ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(cat.id)}
              className={selectedCategory === cat.id ? "bg-[#4A7C6F]" : ""}
            >
              {cat.name}
            </Button>
          ))}
        </div>
      </div>

      {/* Connected Integrations */}
      {configured.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-800">Your Connected Integrations</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {configured.map(config => {
              const integration = catalog.find(i => i.id === config.integration_id) || {
                name: config.integration_id,
                icon: "brain"
              };
              const Icon = ICON_MAP[integration.icon] || Brain;
              
              return (
                <Card key={config.integration_id} className="border-green-200 bg-green-50/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">{integration.name}</p>
                        <p className="text-xs text-green-600">Connected</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleTest(config.integration_id)}
                        disabled={testing}
                      >
                        <Zap className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Integration Catalog */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-800">
          {selectedCategory === "all" ? "All Integrations" : categories.find(c => c.id === selectedCategory)?.name}
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredIntegrations.map(integration => {
            const Icon = ICON_MAP[integration.icon] || Brain;
            const connected = isConfigured(integration.id);
            
            return (
              <Card 
                key={integration.id} 
                className={`border-0 shadow-md hover:shadow-lg transition-shadow ${
                  connected ? "ring-2 ring-green-200" : ""
                }`}
                data-testid={`integration-${integration.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-[#E8F4F0] flex items-center justify-center">
                      <Icon className="w-6 h-6 text-[#4A7C6F]" />
                    </div>
                    {getStatusBadge(connected ? "connected" : integration.status)}
                  </div>
                  
                  <h3 className="font-semibold text-lg text-slate-800 mb-2">{integration.name}</h3>
                  <p className="text-sm text-slate-600 mb-4">{integration.description}</p>
                  
                  {integration.features && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {integration.features.slice(0, 3).map(feature => (
                        <Badge key={feature} variant="outline" className="text-xs">
                          {feature}
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="text-xs text-slate-500">
                      {integration.pricing && <span>{integration.pricing}</span>}
                    </div>
                    
                    {integration.status === "connected" || connected ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleTest(integration.id)}
                        disabled={testing}
                      >
                        {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
                        Test
                      </Button>
                    ) : integration.status === "available" ? (
                      <Button 
                        size="sm"
                        onClick={() => handleConfigure(integration)}
                        className="bg-[#4A7C6F] hover:bg-[#3D6B5F]"
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        Configure
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" disabled>
                        Coming Soon
                      </Button>
                    )}
                  </div>
                  
                  {integration.note && (
                    <p className="text-xs text-[#4A7C6F] mt-3 flex items-center gap-1">
                      <Shield className="w-3 h-3" />
                      {integration.note}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* No Results */}
      {filteredIntegrations.length === 0 && (
        <Card className="border-0 shadow-md">
          <CardContent className="py-12 text-center">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-semibold text-slate-700 mb-2">No integrations found</h3>
            <p className="text-slate-500">Try adjusting your search or filters</p>
          </CardContent>
        </Card>
      )}

      {/* Configuration Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#4A7C6F]" />
              Configure {selectedIntegration?.name}
            </DialogTitle>
            <DialogDescription>
              Enter your API credentials to connect this integration
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {selectedIntegration?.requires_key && (
              <div className="space-y-2">
                <Label htmlFor="api_key">API Key</Label>
                <Input
                  id="api_key"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter your API key"
                />
                <p className="text-xs text-slate-500">
                  Get your API key from your {selectedIntegration?.name} account settings
                </p>
              </div>
            )}

            <div className="p-4 bg-[#E8F4F0] rounded-lg">
              <h4 className="font-medium text-[#4A7C6F] mb-2">Setup Steps</h4>
              <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                <li>Create a {selectedIntegration?.name} account if needed</li>
                <li>Go to your account settings</li>
                <li>Generate an API key</li>
                <li>Paste it above and click Save</li>
              </ol>
            </div>

            {selectedIntegration?.documentation_url && (
              <a 
                href={selectedIntegration.documentation_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#4A7C6F] hover:underline flex items-center gap-1"
              >
                <ExternalLink className="w-4 h-4" />
                View Documentation
              </a>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveConfig}
              disabled={configuring || (selectedIntegration?.requires_key && !apiKey)}
              className="bg-[#4A7C6F] hover:bg-[#3D6B5F]"
            >
              {configuring ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
